import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowUpRight, Building2, CheckCircle2, ChevronDown, CircleAlert,
    ExternalLink, Lightbulb, MessageCircle, ShieldCheck, Smartphone, XCircle,
} from "lucide-react";
import { useReduxLeads } from "@Hooks/reduxLeadsHook/useReduxLeads";
import "./BusinessInfoPage.scss";

const STATUS_META = {
    BLOCKED: {
        label: "Sending stopped",
        description: "Messages cannot currently be sent",
        modifier: "blocked",
        icon: XCircle,
    },
    LIMITED: {
        label: "Partially working",
        description: "Some messaging features are restricted",
        modifier: "limited",
        icon: AlertCircle,
    },
    AVAILABLE: {
        label: "Working normally",
        description: "All messaging features are available",
        modifier: "available",
        icon: CheckCircle2,
    },
};

const ENTITY_META = {
    WABA: {
        label: "Messaging",
        description: "WhatsApp Business Account",
        icon: MessageCircle,
        fixUrl:
            "https://business.facebook.com/latest/billing_hub/accounts?business_id=",
    },
    BUSINESS: {
        label: "Business information",
        description: "Business verification & details",
        icon: Building2,
        fixUrl:
            "https://business.facebook.com/latest/settings/setup_guide/?nav_ref=bm_settings_redirect_migration&bm_redirect_migration=true&business_id=",
    },
    APP: {
        label: "App connection",
        description: "Meta app configuration",
        icon: Smartphone,
        fixUrl: "https://developers.facebook.com/apps",
    },
};

const STATUS_COPY = {
    BLOCKED: "You can't send messages right now.",
    LIMITED: "Messaging is available, but some features are restricted.",
    AVAILABLE: "Your account is healthy and ready to send messages.",
};

const WARNING_COPY = {
    BLOCKED:
        "Messaging is currently stopped. Resolve the issues below to restore sending.",
    LIMITED:
        "Some messaging capabilities are restricted. Review the issues below to restore full access.",
};

const ERROR_MESSAGES = {
    141006: {
        description: "Your payment method isn't working.",
        solution:
            "Add a valid payment method to your Meta account so messages can be sent.",
    },
    141010: {
        description: "Your business identity hasn't been confirmed yet.",
        solution:
            "Go to Business Settings and complete the verification steps. It usually takes a few minutes.",
    },
};

function getFriendlyError(error) {
    const mapped = ERROR_MESSAGES[error.error_code];

    return {
        description:
            mapped?.description ??
            error.error_description ??
            "An issue was detected with this account.",
        solution:
            mapped?.solution ??
            error.possible_solution ??
            "Review your Meta Business settings and resolve the issue.",
    };
}

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.AVAILABLE;
    const Icon = meta.icon;

    return (
        <span className={`status-badge status-badge--${meta.modifier}`}>
            <Icon size={14} strokeWidth={2.5} />
            {meta.label}
        </span>
    );
}

function HealthSummary({ status, counts }) {
    const meta = STATUS_META[status] || STATUS_META.AVAILABLE;
    const Icon = meta.icon;

    return (
        <section className={`health-summary health-summary--${meta.modifier}`}>
            <div className="health-summary__main">
                <div className="health-summary__icon">
                    <Icon size={24} />
                </div>

                <div className="health-summary__content">
                    <span className="eyebrow">Account health</span>

                    <div className="health-summary__title-row">
                        <h2>{meta.label}</h2>
                        <StatusBadge status={status} />
                    </div>

                    <p>{STATUS_COPY[status]}</p>
                </div>
            </div>

            <div className="health-summary__stats">
                <div className="health-stat health-stat--blocked">
                    <span className="health-stat__number">
                        {counts.blocked}
                    </span>
                    <span className="health-stat__label">Stopped</span>
                </div>

                <div className="health-stat health-stat--limited">
                    <span className="health-stat__number">
                        {counts.limited}
                    </span>
                    <span className="health-stat__label">Restricted</span>
                </div>

                <div className="health-stat health-stat--available">
                    <span className="health-stat__number">
                        {counts.available}
                    </span>
                    <span className="health-stat__label">Healthy</span>
                </div>
            </div>

            {WARNING_COPY[status] && (
                <div className="health-summary__warning">
                    <CircleAlert size={18} />
                    <span>{WARNING_COPY[status]}</span>
                </div>
            )}
        </section>
    );
}

function ReviewCard({ reviewStatus, businessId }) {
    const isApproved = reviewStatus === "APPROVED";

    return (
        <section className="review-card">
            <div className="review-card__header">
                <div className="review-card__icon">
                    <ShieldCheck size={20} />
                </div>

                <div>
                    <span className="eyebrow">Business verification</span>
                    <h3>Review status</h3>
                </div>
            </div>

            <div className="review-card__status">
                {isApproved ? (
                    <div className="review-approved">
                        <CheckCircle2 size={18} />
                        <span>Approved</span>
                    </div>
                ) : (
                    <div className="review-pending">
                        <CircleAlert size={18} />
                        <span>Verification required</span>
                    </div>
                )}
            </div>

            {!isApproved && (
                <p className="review-card__description">
                    Complete your Meta business verification to unlock all
                    available features.
                </p>
            )}

            {!isApproved ? (
                <button
                    type="button"
                    className="button button--primary"
                    onClick={() =>
                        window.open(
                            `https://business.facebook.com/settings/security?business_id=${businessId}`,
                            "MetaVerification",
                            "width=900,height=700"
                        )
                    }
                >
                    Start verification
                    <ArrowUpRight size={16} />
                </button>
            ) : (
                <div className="review-card__approved-message">
                    Your business verification is complete.
                </div>
            )}
        </section>
    );
}

function EntityCard({ entity, index, businessId }) {

    const hasErrors = entity.errors?.length > 0;
    const [expanded, setExpanded] = useState(hasErrors);
    const status = STATUS_META[entity.can_send_message] || STATUS_META.AVAILABLE;
    const meta = ENTITY_META[entity.entity_type] || ENTITY_META.BUSINESS;
    const EntityIcon = meta.icon;

    return (
        <article
            className={`entity-card entity-card--${status.modifier}`}
            style={{ "--animation-delay": `${index * 70}ms` }}
        >
            <button
                type="button"
                className={`entity-card__header ${
                    expanded ? "entity-card__header--expanded" : ""
                }`}
                onClick={() =>
                    hasErrors && setExpanded((current) => !current)
                }
                disabled={!hasErrors}
            >
                <div className="entity-card__identity">
                    <div
                        className={`entity-card__icon entity-card__icon--${status.modifier}`}
                    >
                        <EntityIcon size={19} />
                    </div>

                    <div className="entity-card__info">
                        <div className="entity-card__name">
                            {meta.label}
                        </div>

                        <div className="entity-card__description">
                            {meta.description}
                        </div>

                        <div className="entity-card__id">
                            ID: {entity.id}
                        </div>
                    </div>
                </div>

                <div className="entity-card__actions">
                    <StatusBadge status={entity.can_send_message} />

                    {hasErrors && (
                        <span
                            className={`entity-card__chevron ${
                                expanded
                                    ? "entity-card__chevron--open"
                                    : ""
                            }`}
                        >
                            <ChevronDown size={17} />
                        </span>
                    )}
                </div>
            </button>

            {expanded && hasErrors && (
                <div className="entity-card__body">
                    {entity.errors.map((error, errorIndex) => {
                        const { description, solution } =
                            getFriendlyError(error);

                        return (
                            <div
                                className="issue"
                                key={`${error.error_code}-${errorIndex}`}
                            >
                                <div className="issue__content">
                                    <div className="issue__row">
                                        <div className="issue__icon">
                                            <AlertCircle size={17} />
                                        </div>

                                        <div>
                                            <span className="issue__label">
                                                Issue detected
                                            </span>

                                            <p className="issue__description">
                                                {description}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="issue__solution">
                                        <Lightbulb size={17} />

                                        <div>
                                            <span>Recommended action</span>
                                            <p>{solution}</p>
                                        </div>
                                    </div>
                                </div>

                                <a
                                    href={meta.fixUrl + businessId}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`issue__button issue__button--${status.modifier}`}
                                >
                                    Fix issue
                                    <ExternalLink size={15} />
                                </a>
                            </div>
                        );
                    })}
                </div>
            )}
        </article>
    );
}

export default function BusinessInfoPage() {

    const { currentLeads, leadsPaymentInfo } = useReduxLeads();
    const [entities, setEntities] = useState([]);
    const [overallStatus, setOverallStatus] = useState("AVAILABLE");

    useEffect(() => {
        setOverallStatus(leadsPaymentInfo?.overallStatus || "AVAILABLE");
        setEntities(leadsPaymentInfo?.health_status?.entities || []);
    }, [leadsPaymentInfo]);

    const counts = useMemo(
        () => ({
            blocked: entities.filter(
                (entity) => entity.can_send_message === "BLOCKED"
            ).length,

            limited: entities.filter(
                (entity) => entity.can_send_message === "LIMITED"
            ).length,

            available: entities.filter(
                (entity) => entity.can_send_message === "AVAILABLE"
            ).length,
        }),
        [entities]
    );

    const businessId = currentLeads?.businessId;

    const hasIssues = entities.some(
        (entity) => entity.errors?.length > 0
    );

    return (
        <main className="business-page">
            <div className="business-page__container">
                <header className="page-header">
                    <div className="page-header__identity">
                        <div className="page-header__icon">
                            <Building2 size={21} />
                        </div>

                        <div>
                            <span className="eyebrow">Meta Business</span>
                            <h1>Account health</h1>
                            <p>
                                Monitor your messaging account and resolve
                                issues affecting your access.
                            </p>
                        </div>
                    </div>

                    <a
                        href={`https://business.facebook.com/latest/settings/business_info?business_id=${businessId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="button button--secondary"
                    >
                        Business settings
                        <ExternalLink size={15} />
                    </a>
                </header>

                <div className="dashboard-grid">
                    <div className="dashboard-grid__main">
                        <HealthSummary
                            status={overallStatus}
                            counts={counts}
                        />

                        <section className="entities-section">
                            <div className="section-heading">
                                <div>
                                    <span className="eyebrow">
                                        System components
                                    </span>

                                    <h2>Account status</h2>
                                </div>

                                {hasIssues && (
                                    <span className="section-heading__count">
                                        {entities.filter(
                                            (entity) =>
                                                entity.errors?.length > 0
                                        ).length}{" "}
                                        need attention
                                    </span>
                                )}
                            </div>

                            <div className="entities-list">
                                {entities.length > 0 ? (
                                    entities.map((entity, index) => (
                                        <EntityCard
                                            key={entity.id}
                                            entity={entity}
                                            index={index}
                                            businessId={businessId}
                                        />
                                    ))
                                ) : (
                                    <div className="empty-state">
                                        <CheckCircle2 size={28} />
                                        <h3>No account issues detected</h3>
                                        <p>
                                            Your account health information
                                            will appear here.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    <aside className="dashboard-grid__sidebar">
                        <ReviewCard
                            reviewStatus={
                                leadsPaymentInfo?.account_review_status
                            }
                            businessId={businessId}
                        />

                        <section className="help-card">
                            <div className="help-card__icon">
                                <MessageCircle size={20} />
                            </div>

                            <div>
                                <span className="eyebrow">Need help?</span>
                                <h3>Still stuck?</h3>
                                <p>
                                    Contact Meta support if you've resolved
                                    an issue but your account is still
                                    restricted.
                                </p>
                            </div>

                            <a
                                href={`https://business.facebook.com/latest/settings/setup_guide/?nav_ref=bm_settings_redirect_migration&bm_redirect_migration=true&business_id=${businessId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="help-card__link"
                            >
                                Contact Meta support
                                <ArrowUpRight size={15} />
                            </a>
                        </section>
                    </aside>
                </div>
            </div>
        </main>
    );
}
